import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MdCreditCard, MdCheckCircle, MdHourglassTop, MdHistory,
  MdLock, MdRocketLaunch, MdExpandMore, MdExpandLess, MdLocalOffer, MdClose,
  MdWarningAmber, MdBlock, MdInfoOutline,
} from 'react-icons/md';
import * as subSvc from '../../services/subscriptionService';
import { useCalendar } from '../../context/CalendarContext';
import useTheme from '../../context/ThemeContext';
import { formatBsAd, formatAd, formatBs } from '../../utils/calendar';
import { classifySubscriptionStatus } from '../../utils/subscriptionStatus';
import PlatformPaymentModal from '../../components/PlatformPaymentModal';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtNPR  = (n) => `NPR ${Number(n).toLocaleString()}`;

// Add `n` CALENDAR months for the "what you get if you pay" preview — mirrors the backend's
// real addMonths (setMonth-based, clamps to month end) exactly, same convention already used
// in PlatformPaymentModal.jsx's addMonthsDisplay. DISPLAY ESTIMATE only — the server recomputes
// the authoritative date on actual payment, using this identical rule.
const previewAddMonths = (date, n) => {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + Number(n));
  if (d.getDate() < day) d.setDate(0);
  return d;
};

const NEUTRAL_PILL = 'bg-nav-hover text-muted border border-divider';

const STATUS_PILL = {
  Active:             'bg-emerald-500/15 text-emerald-400',
  Trial:              'bg-sky-500/15 text-sky-400',
  'Past Due':         'bg-amber-500/15 text-amber-400',
  'Payment overdue':  'bg-red-500/15 text-red-400',
  Suspended:          'bg-red-500/15 text-red-400',
  'Trial Expired':    'bg-red-500/15 text-red-400',
  Cancelled:          NEUTRAL_PILL,
  Archived:           NEUTRAL_PILL,
};

function StatusPill({ label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_PILL[label] || NEUTRAL_PILL}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label || '—'}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="font-medium text-secondary text-right">{value}</dd>
    </div>
  );
}

// Semantic text colours for the Status row value — reuse the existing status shades.
const STATUS_TEXT = {
  Active:            'text-emerald-400',
  Trial:             'text-sky-400',
  'Past Due':        'text-amber-400',
  'Payment overdue': 'text-red-400',
  Suspended:         'text-red-400',
  'Trial Expired':   'text-red-400',
};

// One row in the Current Plan stacked list: label on the left (muted), value on the
// right (medium weight). Rows separated by a thin divider; last row has none.
function StackRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-divider last:border-0">
      <span className="text-[13px] text-muted flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-secondary text-right">{value}</span>
    </div>
  );
}

// "2083 Shrawan 18 · 03 Aug 2026" — BS primary then AD muted secondary, on one line.
function AdBsValue({ date }) {
  if (!date) return '—';
  return <>{formatBs(date)} <span className="text-muted font-normal">· {formatAd(date)}</span></>;
}

const PLATFORM_PILL = {
  pending:   { label: 'Pending verification', cls: 'bg-amber-500/15 text-amber-400' },
  verified:  { label: 'Verified',             cls: 'bg-emerald-500/15 text-emerald-400' },
  rejected:  { label: 'Rejected',             cls: 'bg-red-500/15 text-red-400' },
  completed: { label: 'Completed',            cls: 'bg-emerald-500/15 text-emerald-400' },
  failed:    { label: 'Failed',               cls: 'bg-red-500/15 text-red-400' },
  refunded:  { label: 'Refunded',             cls: NEUTRAL_PILL },
};

function PlatformStatusPill({ status }) {
  const cfg = PLATFORM_PILL[status] || PLATFORM_PILL.pending;
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
}

// Status label depends on the record source: platform proofs use
// "Pending verification"; older billing records use plain "Pending".
function statusMeta(kind, status) {
  if (kind === 'platform') return PLATFORM_PILL[status] || PLATFORM_PILL.pending;
  const billing = {
    completed: { label: 'Completed', cls: 'bg-emerald-500/15 text-emerald-400' },
    pending:   { label: 'Pending',   cls: 'bg-amber-500/15 text-amber-400' },
    failed:    { label: 'Failed',    cls: 'bg-red-500/15 text-red-400' },
    refunded:  { label: 'Refunded',  cls: NEUTRAL_PILL },
  };
  return billing[status] || { label: status, cls: NEUTRAL_PILL };
}

function UnifiedStatusPill({ kind, status }) {
  const cfg = statusMeta(kind, status);
  return <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
}

// Receipt-style details for one platform subscription payment (club's own record).
function PaymentDetailsModal({ payment, onClose }) {
  const p = payment;
  const period = (p.periodStart || p.periodEnd)
    ? `${formatBsAd(p.periodStart)} – ${formatBsAd(p.periodEnd)}`
    : '—';
  const isPdf = (p.proofMime || '').includes('pdf') || /\.pdf($|\?)/i.test(p.proofUrl || '');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-card border border-divider rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider">
          <h2 className="text-lg font-bold text-secondary">Receipt Details</h2>
          <button onClick={onClose} className="p-1 rounded-lg text-muted hover:bg-nav-hover" aria-label="Close"><MdClose className="text-xl" /></button>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl font-extrabold text-secondary">NPR {Number(p.amount).toLocaleString()}</span>
            <PlatformStatusPill status={p.status} />
          </div>
          <dl className="space-y-2 text-sm">
            <InfoRow label="Plan" value={(p.plan || 'pro').toUpperCase()} />
            <InfoRow label="Payment Method" value={<span className="capitalize">{p.method}</span>} />
            <InfoRow label="Reference ID" value={p.referenceId || '—'} />
            {p.months != null && (
              <InfoRow label="Months" value={p.months > 1
                ? `${p.months} months${p.discountPercent ? ` · ${p.discountPercent}% off` : ''}`
                : '1 month'} />
            )}
            <InfoRow label="Period" value={period} />
            <InfoRow label="Submitted" value={<AdBsValue date={p.submittedAt} />} />
            {p.verifiedAt && <InfoRow label={p.status === 'rejected' ? 'Rejected' : 'Verified'} value={<AdBsValue date={p.verifiedAt} />} />}
            <InfoRow label="Proof uploaded" value={p.proofUrl ? 'Yes' : 'No'} />
            {p.notes && <InfoRow label="Notes" value={p.notes} />}
          </dl>

          {p.status === 'rejected' && p.rejectionReason && (
            <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3">
              <p className="text-sm text-red-400"><strong>Rejection reason:</strong> {p.rejectionReason}</p>
            </div>
          )}

          {p.proofUrl && (
            <a
              href={p.proofUrl} target="_blank" rel="noreferrer"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-card border border-divider text-sm font-medium text-secondary hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200"
            >
              <MdCreditCard className="text-base" /> View Payment Proof {isPdf ? '(PDF)' : ''}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Payment modal ─────────────────────────────────────────────────────────────

function PaymentModal({ onClose, onSuccess }) {
  const currentTheme = useTheme()?.currentTheme;

  // Single source of truth for the Pro price is backend/config/plans.js — fetched here rather
  // than hardcoded, so this modal can never drift from it.
  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn:  () => subSvc.getPlans().then(r => r.data.plans),
    staleTime: 1000 * 60 * 10,
  });
  const proPrice = plansData?.pro?.price;
  const proPriceLabel = proPrice != null ? `NPR ${proPrice.toLocaleString()}` : 'NPR —';

  const mutation = useMutation({
    mutationFn: () => subSvc.initiatePayment({ plan: 'pro' }),
    onSuccess: (res) => {
      toast.success('Payment request created! Transfer funds and send proof on WhatsApp.');
      onSuccess(res.data);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to create payment request'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-divider rounded-2xl shadow-2xl p-6">
        <h2 className="text-lg font-bold text-secondary mb-1">Upgrade to Pro</h2>
        <p className="text-sm text-muted mb-5">{proPriceLabel}/month · Unlimited members</p>

        <div className="bg-nav-hover/40 border border-divider rounded-xl p-4 mb-5 space-y-2 text-sm">
          <p className="font-semibold text-secondary mb-3">Bank Transfer Details</p>
          {[
            ['Bank',           'Siddhartha Bank'],
            ['Account Name',   'Anush Dhakal'],
            ['Account Number', '00818287510'],
            ['Amount',         proPriceLabel],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between">
              <span className="text-muted">{label}</span>
              <span className="font-medium text-secondary">{value}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted mb-4">
          After transferring, click <strong className="text-secondary">Create Request</strong>, then send your proof on
          WhatsApp (+61 414934174). We confirm within 1–2 business days and activate your Pro plan immediately.
        </p>

        <div className="flex gap-2 mb-4">
          {['Khalti', 'eSewa'].map(gw => (
            <div key={gw} className="flex-1 relative flex items-center justify-center gap-2 py-2 rounded-xl border border-dashed border-divider text-xs text-subtle cursor-not-allowed select-none">
              {gw}
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-500/20 text-amber-400 text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap">
                Coming Soon
              </span>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-full bg-card border border-divider text-sm font-medium text-secondary hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className={`flex-1 py-2.5 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-60`}
          >
            {mutation.isPending ? 'Creating…' : 'Create Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SubscriptionPage() {
  const qc = useQueryClient();
  const currentTheme = useTheme()?.currentTheme;
  const { formatDate } = useCalendar();
  const [showHistory, setShowHistory] = useState(false);
  const [showModal,   setShowModal]   = useState(false);
  const [showPay,     setShowPay]     = useState(false);
  const [detailPay,   setDetailPay]   = useState(null);

  const { data: subData, isPending } = useQuery({
    queryKey: ['my-subscription'],
    queryFn:  () => subSvc.getMySubscription().then(r => r.data),
  });

  const { data: historyData } = useQuery({
    queryKey: ['payment-history'],
    queryFn:  () => subSvc.getPaymentHistory().then(r => r.data),
    enabled:  showHistory,
  });

  const { data: discountData } = useQuery({
    queryKey: ['my-discount'],
    queryFn:  () => subSvc.getMyDiscount().then(r => r.data),
  });
  const activeDiscount = discountData?.appliedDiscount || null;

  // Single source of truth for the Pro price is backend/config/plans.js — fetched here rather
  // than hardcoded, so this page can never drift from it.
  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn:  () => subSvc.getPlans().then(r => r.data.plans),
    staleTime: 1000 * 60 * 10,
  });
  const proPrice = plansData?.pro?.price;
  const proPriceLabel = proPrice != null ? `NPR ${proPrice.toLocaleString()}` : 'NPR —';

  const { data: platformPayData } = useQuery({
    queryKey: ['my-platform-payments'],
    queryFn:  () => subSvc.getMyPlatformPayments().then(r => r.data),
  });
  const platformPayments = platformPayData?.payments || [];

  const sub      = subData?.subscription;
  const payments = historyData?.payments || [];
  const isPro    = sub?.plan === 'pro';

  // Unified Billing History: platform subscription proofs (pending/verified/rejected)
  // + older billing records (SubscriptionPayment). These are distinct documents
  // (verifying a proof does not create a SubscriptionPayment), so nothing is
  // shown twice. Sorted newest-first.
  const billingRows = [
    ...platformPayments.map((p) => ({
      kind: 'platform',
      date: p.submittedAt,
      plan: p.plan, amount: p.amount, status: p.status,
      periodStart: p.periodStart, periodEnd: p.periodEnd,
      method: p.method, referenceId: p.referenceId,
      detail: p,
    })),
    ...payments.map((p) => ({
      kind: 'billing',
      date: p.paidAt || p.createdAt,
      plan: p.plan, amount: p.amount, status: p.status,
      periodStart: p.periodStart, periodEnd: p.periodEnd,
      method: p.method, referenceId: p.transactionId,
      detail: {
        plan: p.plan, amount: p.amount, status: p.status, method: p.method,
        referenceId: p.transactionId, notes: p.notes,
        submittedAt: p.paidAt || p.createdAt,
        verifiedAt: p.status === 'completed' ? p.paidAt : null,
        periodStart: p.periodStart, periodEnd: p.periodEnd,
        proofUrl: null,
      },
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));
  const isFree   = !isPro;

  if (isPending) {
    return (
      <div className="space-y-4 max-w-3xl mx-auto">
        {[1,2,3].map(i => <div key={i} className="h-28 rounded-2xl animate-pulse bg-nav-hover" />)}
      </div>
    );
  }

  // ── Status classification — extracted to utils/subscriptionStatus.js so it's unit-testable
  // without a DOM: ACTIVE is a positive check, not the silent default; anything unmatched gets
  // the explicit 'fallback' state rather than being misread as "Active". ───────────────────────
  const now = new Date();
  const {
    state: subState, trialLeft, trialPct,
    daysSinceTrialExpired, daysUntilPause, daysAgo, isExpiringSoon, expiresToday,
  } = classifySubscriptionStatus(sub, now);
  const isTrialState        = subState === 'trial';
  const isTrialExpiredState = subState === 'trialExpired';
  const isGraceWindow       = subState === 'graceWindow';
  const isSuspendedState    = subState === 'suspended';
  const isActivePaidState   = subState === 'activePaid';
  const isPlainFreeState    = subState === 'plainFree';

  // ── Payment preview — same rule the server uses on an actual payment (later of current end /
  // now, +1 calendar month). Shared by the trial, trial-expired, suspended, and active branches
  // below so the number is computed once, consistently. Display only. ──────────────────────────
  const payBase = (sub?.subscriptionEndsAt && new Date(sub.subscriptionEndsAt) > now)
    ? new Date(sub.subscriptionEndsAt) : now;
  const payPreviewEnd  = previewAddMonths(payBase, 1);
  const payAmount      = activeDiscount?.finalMonthlyPrice ?? proPrice;
  const payAmountLabel = payAmount != null ? fmtNPR(payAmount) : proPriceLabel;

  // ── Header action button — "Upgrade" before any real Pro billing exists, "Pay Now" once it
  // does (recovering from overdue/suspended counts as "already billing"). No button at all for
  // the unmatched fallback state (nothing actionable there). ───────────────────────────────────
  const showUpgradeButton = isTrialState || isTrialExpiredState || isPlainFreeState;
  const showPayNowButton  = isActivePaidState || isSuspendedState || isGraceWindow;

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-secondary">Subscription</h1>
        <p className="text-sm text-muted mt-0.5">Manage your TrainAxis plan and billing</p>
      </div>

      {/* ── SECTION 1 — Current status card ── */}
      <div className="bg-card rounded-2xl border border-divider shadow-sm p-6">
        {/* Header row: identity (left) + primary action (right) */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isPro ? 'bg-secondary/10' : 'bg-nav-hover'}`}>
              <MdCreditCard className={`text-xl ${isPro ? 'text-secondary' : 'text-subtle'}`} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted font-medium uppercase tracking-wide">Current Plan</p>
              <div className="flex items-center gap-2.5 mt-0.5">
                <h2 className="text-xl font-bold text-secondary">
                  {isPro ? 'Pro Plan' : 'Free Plan'}
                </h2>
                {sub?.statusDisplay && <StatusPill label={sub.statusDisplay} />}
              </div>
            </div>
          </div>

          <div className="flex-shrink-0">
            {showUpgradeButton && (
              <button
                onClick={() => setShowModal(true)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 transition-all duration-200`}
              >
                <MdRocketLaunch className="text-base" />
                Upgrade
              </button>
            )}
            {showPayNowButton && (
              <button
                onClick={() => setShowPay(true)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} text-sm font-medium hover:opacity-90 transition-all duration-200`}
              >
                <MdCreditCard className="text-base" /> Pay Now
              </button>
            )}
          </div>
        </div>

        {/* ── TRIAL — on trial, any plan. Unified copy: every trial follows the same clock
            regardless of which plan was picked at signup (jobs/subscriptionCron.js never
            branches on plan for the trial→trial_expired→suspended progression). ──────────── */}
        {isTrialState && (
          <div className="mt-5 pt-5 border-t border-divider">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-sky-500/15 flex items-center justify-center flex-shrink-0">
                <MdHourglassTop className="text-sky-400 text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-secondary">
                  On trial — {formatBsAd(sub?.trialEndsAt)}
                </p>
                <p className={`text-sm font-medium mt-0.5 ${trialLeft <= 3 ? 'text-amber-400' : 'text-muted'}`}>
                  {trialLeft} day{trialLeft === 1 ? '' : 's'} left
                </p>
              </div>
            </div>
            <div className="w-full h-2 bg-nav-hover rounded-full overflow-hidden mt-3">
              <div
                className={`h-full rounded-full transition-all ${trialLeft <= 3 ? 'bg-amber-400' : 'bg-sky-500'}`}
                style={{ width: `${trialPct}%` }}
              />
            </div>
            <div className="mt-4 rounded-xl border border-divider bg-nav-hover/40 px-4 py-3">
              <p className="text-sm text-secondary">
                Upgrade to Pro for <strong>{proPriceLabel}</strong>/month any time to keep full access after your trial.
              </p>
              <p className="text-sm text-muted mt-1.5">Nothing is deleted if you don't.</p>
            </div>
          </div>
        )}

        {/* ── TRIAL EXPIRED — any plan, same reasoning as above. ─────────────────────────── */}
        {isTrialExpiredState && (
          <div className="mt-5 pt-5 border-t border-divider">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                <MdWarningAmber className="text-amber-400 text-lg" />
              </div>
              <div>
                <p className="text-base font-semibold text-secondary">
                  Your trial ended — {formatBsAd(sub?.trialEndsAt)}
                </p>
                <p className="text-sm text-amber-400 font-medium mt-0.5">
                  {daysSinceTrialExpired != null ? `${daysSinceTrialExpired} day${daysSinceTrialExpired === 1 ? '' : 's'} ago` : '—'}
                </p>
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-divider bg-nav-hover/40 px-4 py-3">
              <p className="text-sm text-secondary">
                Upgrade to Pro for <strong>{proPriceLabel}</strong>/month to keep access.
                {' '}{daysUntilPause > 0
                  ? `You have ${daysUntilPause} day${daysUntilPause === 1 ? '' : 's'} before access pauses`
                  : 'Access may pause any time now'}
                {' '}— your students, payments and records stay exactly as they are.
              </p>
            </div>
          </div>
        )}

        {/* Past-due grace window (status still active/past_due, days overdue within grace) —
            unchanged, pre-existing state, not one of the states this rewrite targets. Already
            shows a correct "Overdue by N days" (isOverdue gates it). */}
        {isGraceWindow && (
          <>
            <div className="mt-5 pt-5 border-t border-divider">
              <StackRow
                label="Status"
                value={<span className={`font-medium ${STATUS_TEXT[sub?.statusDisplay] || 'text-secondary'}`}>{sub?.statusDisplay || '—'}</span>}
              />
              <StackRow
                label="Days Remaining"
                value={<span className="text-red-400 font-medium">Overdue by {sub.overdueDays} day{sub.overdueDays === 1 ? '' : 's'}</span>}
              />
              <StackRow label="Subscription Ends" value={<AdBsValue date={sub?.subscriptionEndsAt} />} />
              <StackRow label="Next Payment Due"  value={<AdBsValue date={sub?.nextRenewalDate} />} />
              <StackRow
                label="Grace Period Remaining"
                value={<span className="text-red-400 font-medium">
                  {sub.graceDaysRemaining > 0
                    ? `${sub.graceDaysRemaining} day${sub.graceDaysRemaining === 1 ? '' : 's'}`
                    : 'Suspension imminent'}
                </span>}
              />
            </div>
            <div className="mt-5 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3">
              <p className="text-sm font-semibold text-red-400">
                {sub.isFinalGraceDay
                  ? 'Final reminder: your subscription will be suspended after today if payment is not completed.'
                  : 'Payment required — your subscription is overdue. Complete payment to avoid suspension.'}
              </p>
            </div>
          </>
        )}

        {/* ── LAPSED/SUSPENDED ────────────────────────────────────────────────────────────── */}
        {isSuspendedState && (
          <div className="mt-5 pt-5 border-t border-divider">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <MdBlock className="text-red-400 text-lg" />
              </div>
              <div>
                <p className="text-base font-semibold text-secondary">
                  Your access stopped on {formatBsAd(sub?.subscriptionEndsAt)}
                </p>
                <p className="text-sm text-red-400 font-medium mt-0.5">
                  {daysAgo} day{daysAgo === 1 ? '' : 's'} ago
                </p>
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-divider bg-nav-hover/40 px-4 py-3">
              <p className="text-sm text-secondary">
                <strong>{payAmountLabel}</strong> restores access today and covers you until <strong>{formatBsAd(payPreviewEnd)}</strong>.
              </p>
              <p className="text-sm text-muted mt-1.5">You are not charged for the time you were away.</p>
            </div>
          </div>
        )}

        {/* ── ACTIVE / EXPIRING SOON — positive check: status is literally 'active' AND a real,
            not-yet-passed end date exists. Never the silent default. ────────────────────────── */}
        {isActivePaidState && (
          <div className="mt-5 pt-5 border-t border-divider">
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isExpiringSoon ? 'bg-amber-500/15' : 'bg-emerald-500/15'}`}>
                {isExpiringSoon
                  ? <MdWarningAmber className="text-amber-400 text-lg" />
                  : <MdCheckCircle className="text-emerald-400 text-lg" />}
              </div>
              <div>
                <p className="text-base font-semibold text-secondary">
                  Active until {formatBsAd(sub.subscriptionEndsAt)}
                </p>
                <p className={`text-sm font-medium mt-0.5 ${isExpiringSoon ? 'text-amber-400' : 'text-muted'}`}>
                  {expiresToday ? 'Expires today' : `${sub.daysRemaining} day${sub.daysRemaining === 1 ? '' : 's'} left`}
                </p>
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-divider bg-nav-hover/40 px-4 py-3">
              <p className="text-sm text-secondary">
                <strong>{payAmountLabel}</strong> extends you to <strong>{formatBsAd(payPreviewEnd)}</strong>.
              </p>
              <p className="text-sm text-muted mt-1.5">Your remaining {sub.daysRemaining} day{sub.daysRemaining === 1 ? '' : 's'} are not lost.</p>
            </div>
          </div>
        )}

        {/* ── Plain free plan — no trial, status active, never billed. The one legitimate
            "nothing to see here" case. ───────────────────────────────────────────────────── */}
        {isPlainFreeState && (
          <p className="text-sm text-muted mt-4">
            Free plan · Limited to <strong className="text-secondary">15 members</strong>
          </p>
        )}

        {/* ── Fallback — anything unmatched (e.g. a cancelled/archived club with plan still 'pro',
            reachable only via impersonation since tenant.js blocks normal login for those states).
            Shows the real status plainly. Never claims "Active"; never a blank or negative number. ── */}
        {!isTrialState && !isTrialExpiredState && !isGraceWindow && !isSuspendedState && !isActivePaidState && !isPlainFreeState && (
          <div className="mt-5 pt-5 border-t border-divider">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-nav-hover flex items-center justify-center flex-shrink-0">
                <MdInfoOutline className="text-subtle text-lg" />
              </div>
              <div>
                <p className="text-base font-semibold text-secondary">
                  Status: {sub?.statusLabel || sub?.status || 'Unknown'}
                </p>
                <p className="text-sm text-muted mt-0.5">This club's subscription is no longer active.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Applied Discount Card ── */}
      {activeDiscount && (
        <div className="bg-card rounded-2xl border border-emerald-500/30 shadow-sm p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-500/15">
              <MdLocalOffer className="text-xl text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted font-medium uppercase tracking-wide">Active Discount</p>
              <h2 className="text-base font-bold text-secondary mt-0.5">{activeDiscount.discountId?.name || 'Special Pricing'}</h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted">
                <span>
                  Original: <span className="line-through text-subtle">{fmtNPR(activeDiscount.originalMonthlyPrice)}/mo</span>
                </span>
                <span className="text-emerald-400 font-semibold">
                  Saving {fmtNPR(activeDiscount.discountAmount)}/mo
                </span>
                <span className="font-bold text-secondary">
                  You pay: {fmtNPR(activeDiscount.finalMonthlyPrice)}/mo
                </span>
              </div>
              {activeDiscount.duration === 'forever' && (
                <p className="text-xs text-emerald-400 mt-1.5 font-medium">This discount applies every billing cycle.</p>
              )}
              {activeDiscount.duration === 'repeating' && activeDiscount.expiresAt && (
                <p className="text-xs text-muted mt-1.5">Valid until {fmtDate(activeDiscount.expiresAt)}</p>
              )}
              {activeDiscount.duration === 'once' && (
                <p className="text-xs text-muted mt-1.5">Applies to current billing cycle only.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── SECTION 2 — Upgrade banner (free plan only) ── */}
      {isFree && (
        <div className="bg-card rounded-2xl border border-divider p-6 sm:p-8">
          <div className="max-w-xl">
            <p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">You're on Free</p>
            <h2 className="text-xl sm:text-2xl font-bold leading-snug mb-2 text-secondary">
              You're missing out on the full TrainAxis experience
            </h2>
            <p className="text-muted text-sm mb-6">
              Real clubs need real tools. Upgrade to Pro and unlock everything.
            </p>

            <div className="space-y-3 mb-7">
              {[
                { icon: MdLock, text: 'Unlimited members', sub: "You're limited to 15" },
                { icon: MdLock, text: 'Khalti & eSewa payment collection', sub: 'Collect fees digitally' },
                { icon: MdLock, text: 'Gamification — XP, badges, leagues', sub: 'Keep students motivated & coming back' },
              ].map(({ icon: Icon, text, sub: subText }) => (
                <div key={text} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-nav-hover flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="text-secondary text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-secondary">{text}</p>
                    <p className="text-xs text-subtle">{subText}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowModal(true)}
              className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 transition-all duration-200`}
            >
              <MdRocketLaunch />
              Upgrade to Pro — {proPriceLabel}/month
            </button>
            <p className="text-xs text-subtle mt-2">Cancel anytime. No contracts.</p>
          </div>
        </div>
      )}

      {/* ── SECTION 3 — Plan comparison ── */}
      <div>
        <h2 className="text-base font-bold text-secondary mb-4">Plan Comparison</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-stretch">

          {/* Free */}
          <div className={`rounded-2xl border-2 p-6 flex flex-col ${isFree && !sub?.isTrialing ? 'border-divider bg-nav-hover/40' : 'border-divider bg-card'}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-secondary">Free</h3>
              {isFree && <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${NEUTRAL_PILL}`}>Current plan</span>}
            </div>
            <p className="text-2xl font-extrabold text-secondary leading-none">Free</p>
            <p className="text-xs text-subtle mt-1.5 mb-5">Up to 15 members</p>
            <ul className="space-y-3">
              {['Up to 15 members','Attendance tracking','Belt progress tracking','Basic announcements','Student & trainer management'].map(f => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-muted">
                  <MdCheckCircle className="text-subtle flex-shrink-0 mt-0.5 text-base" />
                  <span>{f}</span>
                </li>
              ))}
              {['Unlimited members','Khalti & eSewa payments','Gamification & XP','Belt exam management','Advanced reports'].map(f => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-subtle">
                  <MdLock className="text-subtle flex-shrink-0 mt-0.5 text-base" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div className={`rounded-2xl border-2 p-6 relative bg-card flex flex-col ${isPro ? 'border-secondary' : 'border-secondary/40'}`}>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-secondary text-card shadow">
                Most Popular
              </span>
            </div>
            <div className="flex items-center justify-between mb-2 mt-1">
              <h3 className="font-bold text-secondary">Pro</h3>
              {isPro && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-nav-hover text-secondary">Current plan</span>}
            </div>
            <div className="flex items-baseline gap-1.5 leading-none">
              <p className="text-2xl font-extrabold text-secondary">{proPriceLabel}</p>
              <span className="text-sm text-subtle">/month</span>
            </div>
            <p className="text-xs text-subtle mt-1.5 mb-5">Unlimited members</p>
            <ul className="space-y-3 flex-1 mb-6">
              {['Unlimited members','Everything in Free','Khalti & eSewa payments','Gamification — XP, Badges, Leagues','Belt exam management','Advanced reports & analytics','Class enrollment system','Priority support','Custom announcements & SMS','Multi-trainer management'].map(f => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-secondary">
                  <MdCheckCircle className="text-emerald-500 flex-shrink-0 mt-0.5 text-base" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {isFree ? (
              <button
                onClick={() => setShowModal(true)}
                className={`w-full py-2.5 rounded-full text-sm font-medium ${currentTheme === 'dark' ? 'bg-primary text-white' : 'text-card bg-secondary'} hover:opacity-90 transition-all duration-200`}
              >
                Upgrade Now
              </button>
            ) : (
              <div className="w-full py-2.5 rounded-full text-center text-sm font-semibold bg-nav-hover text-secondary">
                Your Plan
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Billing History — includes subscription payment proofs (pending/verified/rejected) ── */}
      <div className="bg-card rounded-2xl border border-divider shadow-sm">
        <button
          onClick={() => setShowHistory(p => !p)}
          className="w-full flex items-center gap-2 px-5 py-4 border-b border-divider hover:bg-nav-hover/40 transition-colors rounded-t-2xl"
        >
          <MdHistory className="text-subtle" />
          <span className="text-base font-bold text-secondary flex-1 text-left">Billing History</span>
          {showHistory ? <MdExpandLess className="text-subtle" /> : <MdExpandMore className="text-subtle" />}
        </button>

        {showHistory && (
          billingRows.length === 0 ? (
            <div className="py-10 text-center">
              <MdCreditCard className="text-4xl text-subtle mx-auto mb-2" />
              <p className="text-sm text-subtle">No payments yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-divider text-xs text-muted uppercase tracking-wide">
                    <th className="px-5 py-3.5 text-left font-medium whitespace-nowrap">Date</th>
                    <th className="px-5 py-3.5 text-left font-medium">Plan</th>
                    <th className="px-5 py-3.5 text-left font-medium">Amount</th>
                    <th className="px-5 py-3.5 text-left font-medium">Status</th>
                    <th className="px-5 py-3.5 text-left font-medium">Period</th>
                    <th className="px-5 py-3.5 text-left font-medium">Method</th>
                    <th className="px-5 py-3.5 text-left font-medium">Reference</th>
                    <th className="px-5 py-3.5 text-right font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {billingRows.map((r, i) => (
                    <tr key={i} className="border-b border-divider last:border-0 hover:bg-nav-hover/40">
                      <td className="px-5 py-3.5 text-secondary whitespace-nowrap"><AdBsValue date={r.date} /></td>
                      <td className="px-5 py-3.5 uppercase text-secondary font-medium">{r.plan}</td>
                      <td className="px-5 py-3.5 text-secondary whitespace-nowrap">{fmtNPR(r.amount)}</td>
                      <td className="px-5 py-3.5"><UnifiedStatusPill kind={r.kind} status={r.status} /></td>
                      <td className="px-5 py-3.5 text-muted text-xs whitespace-nowrap">
                        {r.periodStart ? `${formatDate(r.periodStart)} – ${formatDate(r.periodEnd)}` : '—'}
                      </td>
                      <td className="px-5 py-3.5 capitalize text-secondary">{r.method || '—'}</td>
                      <td className="px-5 py-3.5 text-muted text-xs">{r.referenceId || '—'}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={() => setDetailPay(r.detail)} className="text-xs font-semibold text-secondary hover:underline whitespace-nowrap">
                          View details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {showModal && (
        <PaymentModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            qc.invalidateQueries({ queryKey: ['payment-history'] });
            qc.invalidateQueries({ queryKey: ['my-subscription'] });
          }}
        />
      )}

      {showPay && (
        <PlatformPaymentModal
          onClose={() => setShowPay(false)}
          onSubmitted={() => {
            qc.invalidateQueries({ queryKey: ['platform-payment-info'] });
            qc.invalidateQueries({ queryKey: ['my-subscription'] });
            qc.invalidateQueries({ queryKey: ['my-platform-payments'] });
          }}
        />
      )}

      {detailPay && <PaymentDetailsModal payment={detailPay} onClose={() => setDetailPay(null)} />}
    </div>
  );
}
